import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { bloodDropLabel } from './bloodDropLabel';

const bloodDrop = require('../../assets/imgs/blood_drop_black.png');

/**
 * MVX reference-range bar chart. Mirrors the app's ReferenceRangeChart look
 * (horizontal Highcharts bar, green/yellow/orange/red plotBands, boundary-value
 * tick labels, blood-drop marker, grid lines) — but the axis is in VALUE units
 * so the drop lands correctly against the MVX clinical ranges (Schmidt-optimal /
 * lab / severe), which are NOT evenly spaced the way the app's derived ranges are.
 * Self-contained: no PanelMarker, so it can never affect the rest of the report.
 */
export type MvxChartBand = { color: string; from: number; to: number; label?: string };

type Props = {
  domLo: number;
  domHi: number;
  bands: MvxChartBand[];
  /** Value positions to label (band boundaries). */
  ticks: number[];
  value: number;
  /** Format a tick / value for display. */
  format: (n: number) => string;
  /** Show the zone names (Alarm / < Lab / Lab / Optimal / …) above each band. */
  showLabels?: boolean;
};

const GRID_LINE_COLOR = '#CDD7EA';

// Inside a flex/table cell Highcharts first renders at its ~600px default and
// won't shrink on its own — reflow once layout has settled so it fits the cell.
const reflowSoon = (chart: Highcharts.Chart): void => {
  setTimeout(() => {
    try {
      chart.reflow();
    } catch (e) {
      /* chart unmounted */
    }
  }, 0);
};

export class MvxRangeChart extends React.Component<Props> {
  render() {
    const { domLo, domHi, bands, ticks, value, format, showLabels } = this.props;
    const clamped = Math.max(domLo, Math.min(domHi, value));
    // A plotBand whose edge sits exactly on the axis min/max doesn't paint in
    // Highcharts, dropping the leftmost (green) and rightmost (red) bands. Expand
    // the axis a hair so the first/last bands sit STRICTLY inside it and render.
    const edgePad = (domHi - domLo) * 0.04 || 1;
    const axisMin = domLo - edgePad;
    const axisMax = domHi + edgePad;
    const lastBand = bands.length - 1;
    // Nudge the drop label inward so it doesn't clip at the ends (as the app does).
    const offset = clamped >= (domLo + domHi) / 2 ? -12 : 12;

    const options: Highcharts.Options = {
      // Compact row; the zone labels sit INSIDE the colored bands (centered) so
      // the band color shows above and below the label text. marginBottom leaves
      // room for the boundary-value tick labels below the bar.
      chart: {
        type: 'bar',
        height: 50,
        marginTop: 6,
        marginBottom: 20,
        backgroundColor: 'transparent',
      },
      title: { text: '' },
      xAxis: [{ labels: { enabled: false }, tickLength: 0 }],
      yAxis: {
        title: { text: '' },
        min: axisMin,
        max: axisMax,
        tickPositions: ticks,
        labels: {
          enabled: true,
          formatter: function () {
            return format((this as any).value);
          },
          style: { fontSize: '10px', color: '#555' },
        },
        gridLineWidth: 1,
        gridLineColor: GRID_LINE_COLOR,
        plotBands: bands.map((b, i) => ({
          from: i === 0 ? domLo - edgePad * 0.9 : b.from,
          to: i === lastBand ? domHi + edgePad * 0.9 : b.to,
          color: b.color,
          label: showLabels ? { text: b.label ?? '', align: 'center', verticalAlign: 'middle', y: 0, style: { color: '#333', fontSize: '10px', fontWeight: 'bold' } } : undefined,
        })),
      },
      tooltip: { enabled: false },
      series: [
        {
          type: 'bar',
          color: 'rgba(255,255,255,0)',
          borderWidth: 0,
          pointWidth: 10,
          data: [clamped],
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            useHTML: true,
            verticalAlign: 'middle',
            x: offset,
            format: bloodDropLabel(bloodDrop),
          },
        } as Highcharts.SeriesBarOptions,
      ],
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
    };

    // Constrain the chart to the table cell so it can't overflow the panel
    // (without a width the chart falls back to Highcharts' ~600px default).
    return (
      <HighchartsReact
        options={options}
        highcharts={Highcharts}
        callback={reflowSoon}
        containerProps={{ style: { width: '100%', overflow: 'hidden' } }}
      />
    );
  }
}

export default MvxRangeChart;
